import { useQuery, useLazyQuery, gql } from "@apollo/client"
import { offsetLimitPagination } from "@apollo/client/utilities"
import * as Sentry from "@sentry/browser"
import { toId } from "../../utils"
import { KNOWLEDGE_FIELDS } from "../knowledge"

const POSTS_LIMIT = 5

const POSTS_FIELDS = `
    id
    title
    content
    hasConfirm
    hasSentConfirms
    hasComments
    visibleFrom
    locations { id name }
    users { id fullName }
    groups { id name }
    uploads { 
      id 
      key
      fileName
      fileGroup 
      fileSize
    }     
    job { 
      id
      title
      process { 
        id
        repeat { type frequency weekdays { monday tuesday wednesday thursday friday saturday sunday } }
      }
      task { id }
      percentComplete
      availableFrom
      status {
        completed
        completedAt
        completedBy { id fullName firstName lastName avatar { key } }
        steps { 
          id
          step
          name
          description        
          responseType
          response
          completedAt
          completedBy { id fullName firstName lastName avatar { key } }
          uploads { id key fileName fileGroup fileSize }
        }
      }
      location { id timeZone }
      users { id fullName firstName lastName avatar { key } }
      groups { id name } 
      author { id }
    }
    knowledge { ${KNOWLEDGE_FIELDS} }
    confirmed { id confirmed user { id firstName lastName fullName avatar { key } } }
    unconfirmed { id confirmed user { id firstName lastName fullName avatar { key } } }
    author { id firstName lastName fullName avatar { key } }
    comments {
      id 
      content 
      uploads { id key fileName fileGroup fileSize } 
      author { id firstName lastName avatar { key } }
      createdAt 
    }
    createdAt
    updatedAt
`

const POSTS_QUERY = gql`query Posts($set: String, $offset: Int) {
  posts: postsV2(set: $set, limit: ${POSTS_LIMIT}, offset: $offset) {
    ${POSTS_FIELDS}
  } 
}`

const postsQueryTypePolicies = {
  postsV2: offsetLimitPagination(["set"]),
}

const postTypePolicies = {
  Post: {
    fields: {
      confirmed: {
        merge(prev, next) {
          return [...next]
        },
      },
      unconfirmed: {
        merge(prev, next) {
          return [...next]
        },
      },
    },
  },
}

const POST_CREATED_SUBSCRIPTION = gql`
  subscription 
    postCreatedV2 {
      postCreatedV2 {
        ${POSTS_FIELDS}
      }
    }
`
const POST_UPDATED_SUBSCRIPTION = gql`
  subscription 
    postUpdatedV2 {
      postUpdatedV2 {
        ${POSTS_FIELDS}
      }
    }
`

const POST_DELETED_SUBSCRIPTION = gql`
  subscription postDeleted {
    postDeleted
  }
`

const COMMENT_CREATED_SUBSCRIPTION = gql`
  subscription commentCreated {
    commentCreated {
      id
      content
      uploads {
        id
        key
        fileName
        fileGroup
        fileSize
      }
      author {
        id
        firstName
        lastName
        avatar {
          key
        }
      }
      post
      createdAt
    }
  }
`

const loadMore = (result) => async () => {
  try {
    await result.fetchMore({
      variables: {
        ...result.variables,
        offset: result.data.posts.length,
        limit: POSTS_LIMIT,
      },
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const postCreatedSubscriptionUpdateQuery = (prev, { subscriptionData }) => {
  if (!subscriptionData.data) return prev
  const newItem = subscriptionData.data.postCreatedV2
  const next = JSON.parse(JSON.stringify(prev))
  next.posts = [newItem, ...next.posts]
  return next
}

const postUpdatedSubscriptionUpdateQuery = (prev, { subscriptionData }) => {
  if (!subscriptionData.data) return prev
  const updatedItem = subscriptionData.data.postUpdatedV2
  const next = JSON.parse(JSON.stringify(prev)) // { ...prev }
  let post = next.posts.find((p) => toId(p) === toId(updatedItem))
  if (post) {
    post = { ...updatedItem }
  }
  return next
}

const postDeletedSubscriptionUpdateQuery = (prev, { subscriptionData }) => {
  if (!subscriptionData.data) return prev
  const next = JSON.parse(JSON.stringify(prev))
  next.posts = [...prev.posts.filter((post) => toId(post) !== toId(subscriptionData.data.postDeleted))]
  return next
}

const postCommentCreatedSubscriptionUpdateQuery = (prev, { subscriptionData }) => {
  if (!subscriptionData.data) return prev
  const newComment = subscriptionData.data.commentCreated
  const next = JSON.parse(JSON.stringify(prev))
  // find the post
  const post = next.posts.find((p) => toId(p) === toId(newComment.post))
  if (post && !post.comments.find((c) => toId(c) === toId(newComment))) {
    post.comments.push(newComment)
  }
  return next
}

const subscribe =
  ({ subscribeToMore, variables }) =>
  () =>
    [
      subscribeToMore({
        document: POST_CREATED_SUBSCRIPTION,
        variables,
        updateQuery: postCreatedSubscriptionUpdateQuery,
        onError: (err) => console.log("[useQueryPosts][POST_CREATED_SUBSCRIPTION]", err),
      }),
      subscribeToMore({
        document: POST_UPDATED_SUBSCRIPTION,
        variables,
        updateQuery: postUpdatedSubscriptionUpdateQuery,
        onError: (err) => console.log("[useQueryPosts][POST_UPDATED_SUBSCRIPTION]", err),
      }),
      subscribeToMore({
        document: POST_DELETED_SUBSCRIPTION,
        variables,
        updateQuery: postDeletedSubscriptionUpdateQuery,
        onError: (err) => console.log("[useQueryPosts][POST_DELETED_SUBSCRIPTION]", err),
      }),
      subscribeToMore({
        document: COMMENT_CREATED_SUBSCRIPTION,
        variables,
        updateQuery: postCommentCreatedSubscriptionUpdateQuery,
        onError: (err) => console.log("[useQueryPosts][COMMENT_CREATED_SUBSCRIPTION]", err),
      }),
    ]

const useQueryPosts = (options = {}) => {
  const { variables, ...otherOptions } = options
  const result = useQuery(POSTS_QUERY, {
    variables: {
      ...variables,
      offset: 0,
      limit: POSTS_LIMIT,
    },
    notifyOnNetworkStatusChange: true,
    ...otherOptions,
  })

  return {
    ...result,
    loadMore: loadMore(result),
    subscribe: subscribe(result),
  }
}

const useLazyQueryPosts = () => {
  const [loadPosts, result] = useLazyQuery(POSTS_QUERY, {
    variables: {
      offset: 0,
      limit: POSTS_LIMIT,
    },
    notifyOnNetworkStatusChange: true,
  })

  return [
    loadPosts,
    {
      ...result,
      loadMore: loadMore(result),
      subscribe: subscribe(result),
    },
  ]
}

const postMatchesSet = (post, set) => {
  if (set === "all") {
    return true
  }
  if (set === "important" && post.hasConfirm) {
    return true
  }
  if (set === "jobs" && post.job) {
    return true
  }
  if (set === "knowledge" && post.knowledge) {
    return true
  }
  return false
}

export {
  useQueryPosts,
  useLazyQueryPosts,
  POSTS_FIELDS,
  POSTS_QUERY,
  POST_CREATED_SUBSCRIPTION,
  POST_UPDATED_SUBSCRIPTION,
  POST_DELETED_SUBSCRIPTION,
  COMMENT_CREATED_SUBSCRIPTION,
  postTypePolicies,
  postsQueryTypePolicies,
  postCreatedSubscriptionUpdateQuery,
  postUpdatedSubscriptionUpdateQuery,
  postDeletedSubscriptionUpdateQuery,
  postCommentCreatedSubscriptionUpdateQuery,
  postMatchesSet,
}
